import { QueryClient, QueryClientProvider } from '@tanstack/react-query';
import { Link, Outlet, useRouterState } from '@tanstack/react-router';
import { AppShell, Group, MantineProvider, NavLink, Stack, Text, createTheme } from '@mantine/core';
import { Notifications } from '@mantine/notifications';
import {
    IconBug,
    IconClipboardList,
    IconGhost2,
    IconLayoutGrid,
    IconShieldLock,
} from '@tabler/icons-react';
import { DrataLogo } from './components/DrataLogo';
import { SignedInIdentity } from './components/SignedInIdentity';
import { useIsAdmin } from './hooks/useIsAdmin';

const queryClient = new QueryClient({
    defaultOptions: {
        queries: {
            staleTime: 30_000,
            retry: 1,
        },
    },
});

const theme = createTheme({
    primaryColor: 'blue',
    fontFamily: '-apple-system, BlinkMacSystemFont, "Segoe UI", Roboto, Oxygen, Ubuntu, sans-serif',
});

/**
 * Root route layout for the vendor portal (see router.tsx). Owns the provider
 * tree (TanStack Query + Mantine); the chrome + route Outlet live in
 * `<AppShellChrome />` so that any TanStack Query hook it uses (e.g. the
 * admin/identity lookups) runs INSIDE `QueryClientProvider`. Calling `useQuery`
 * here in `App` — where the provider is a child, not an ancestor — throws
 * "No QueryClient set" and white-screens every route (VI-783 regression).
 */
export const App = (): React.JSX.Element => {
    return (
        <QueryClientProvider client={queryClient}>
            <MantineProvider theme={theme} defaultColorScheme="auto">
                <Notifications position="top-right" />
                <AppShellChrome />
            </MantineProvider>
        </QueryClientProvider>
    );
};

/**
 * Header + navbar chrome and the route `<Outlet />`. Rendered inside the
 * provider tree, so it may use TanStack Query hooks. Navigation is URL-driven:
 * the navbar links push routes and their active state derives from the current
 * pathname (VI-513).
 */
const AppShellChrome = (): React.JSX.Element => {
    const pathname = useRouterState({ select: (state) => state.location.pathname });
    const { data: adminStatus } = useIsAdmin();

    return (
        <AppShell header={{ height: 56 }} navbar={{ width: 220, breakpoint: 'sm' }} padding="lg">
            <AppShell.Header
                style={{ backgroundColor: '#00222f', color: 'white', borderBottom: 0 }}
                data-testid="AppShellHeader"
            >
                <Group justify="space-between" align="center" h="100%" px="xl">
                    <Group gap="md" align="center">
                        <DrataLogo />
                        <Text size="lg" fw={600} c="white">
                            Vendor management
                        </Text>
                    </Group>
                    <SignedInIdentity />
                </Group>
            </AppShell.Header>

            <AppShell.Navbar p="md" data-testid="AppShellNavbar">
                <Stack gap="xs">
                    <NavLink
                        component={Link}
                        to="/catalog"
                        label="Company Catalog"
                        description="Promoted vendors"
                        leftSection={<IconLayoutGrid size={18} />}
                        active={pathname.startsWith('/catalog')}
                        data-testid="NavCatalog"
                    />
                    <NavLink
                        component={Link}
                        to="/candidates"
                        label="Candidate Catalog"
                        description="Pre-curation queue"
                        leftSection={<IconClipboardList size={18} />}
                        active={pathname.startsWith('/candidates')}
                        data-testid="NavCandidates"
                    />
                    <NavLink
                        component={Link}
                        to="/orphaned"
                        label="Orphan records"
                        description="Non-curated, no tenants"
                        leftSection={<IconGhost2 size={18} />}
                        active={pathname.startsWith('/orphaned')}
                        data-testid="NavOrphaned"
                    />
                    <NavLink
                        component={Link}
                        to="/troubleshooting"
                        label="Troubleshooting"
                        description="Directory lookups"
                        leftSection={<IconBug size={18} />}
                        active={pathname.startsWith('/troubleshooting')}
                        data-testid="NavTroubleshooting"
                    />
                    {adminStatus?.is_admin ? (
                        <NavLink
                            component={Link}
                            to="/admins"
                            label="Admins"
                            description="Manage admin access"
                            leftSection={<IconShieldLock size={18} />}
                            active={pathname.startsWith('/admins')}
                            data-testid="NavAdmins"
                        />
                    ) : null}
                </Stack>
            </AppShell.Navbar>

            <AppShell.Main
                style={{
                    height: '100dvh',
                    display: 'flex',
                    flexDirection: 'column',
                    overflow: 'hidden',
                }}
            >
                <Outlet />
            </AppShell.Main>
        </AppShell>
    );
};
